import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';

/**
 * 5-Stage Hydroponic Calculation Workflow State Management
 * 
 * Manages the sequential workflow from water analysis to final recipe calculation
 * with proper validation gates and data persistence at each stage.
 */

// Workflow Stage Definitions
export enum WorkflowStage {
  WATER_PROFILE = 'water-profile',
  CROP_FORMULATION = 'crop-formulation', 
  SALT_MANAGEMENT = 'salt-management',
  STOCK_SOLUTIONS = 'stock-solutions',
  UNIFIED_CALCULATOR = 'unified-calculator'
}

// Stage Completion Status
export interface StageStatus {
  completed: boolean;
  validated: boolean;
  dataId?: string; // Database ID of saved data
  timestamp?: string;
  errors?: string[];
  warnings?: string[];
}

// Water Profile Data
export interface WorkflowWaterProfile {
  id?: string;
  name: string;
  ph: number;
  ec: number;
  elements: {
    symbol: string;
    concentration: number;
    unit: string;
  }[];
  analysisResults?: {
    qualityScore: number;
    recommendations: string[];
    compatibility: 'excellent' | 'good' | 'fair' | 'poor';
  };
}

// Crop Formulation Data
export interface WorkflowFormulation {
  id?: string;
  name: string;
  cropType?: string; // Made optional to match Formulation interface
  description?: string;
  targetEc?: number; // Added to match Formulation interface
  targetPh?: number; // Added to match Formulation interface
  volume?: number; // Added to match Formulation interface
  volumeUnit?: string; // Added to match Formulation interface
  elements: {
    symbol: string;
    concentration: number;
    unit: string;
  }[];
  analysisResults?: {
    qualityScore: number;
    recommendations: string[];
    compatibility: 'excellent' | 'good' | 'fair' | 'poor';
  };
  scientificReference?: string;
  ratioAnalysis?: {
    npkRatio: string;
    micronutrientBalance: 'optimal' | 'adequate' | 'deficient';
  };
}

// Salt Selection Data
export interface WorkflowSaltSelection {
  selectedSalts: {
    id: string;
    name: string;
    formula: string;
    elements: { symbol: string; percentage: number }[];
    compatibility: 'beneficial' | 'neutral' | 'caution' | 'incompatible';
  }[];
  customSalts: any[]; // User-created salts
  compatibilityMatrix: {
    [saltId: string]: {
      [otherSaltId: string]: 'beneficial' | 'neutral' | 'caution' | 'incompatible';
    };
  };
}

// Stock Solution Configuration
export interface WorkflowStockSolution {
  type: 'single' | 'partA' | 'partB' | 'partAB';
  volume: number;
  volumeUnit: string;
  configuration: {
    partA?: { salts: string[]; volume: number };
    partB?: { salts: string[]; volume: number };
    single?: { salts: string[]; volume: number };
  };
  dilutionRatio: number;
  workingSolutionVolume: number;
  preparationSteps: string[];
}

// Final Calculation Results
export interface WorkflowCalculationResults {
  recipe: {
    salts: { name: string; amount: number; unit: string }[];
    phAdjustment: { product: string; amount: number; unit: string };
    totalCost: number;
    preparationTime: number;
  };
  predictions: {
    finalPh: number;
    finalEc: number;
    achievedConcentrations: { symbol: string; target: number; achieved: number }[];
    ionicStrength?: number; // Added for HydroBuddy compatibility
    totalDissolvedSolids?: number; // Added for HydroBuddy compatibility
  };
  instructions: {
    stockPreparation: string[];
    workingSolution: string[];
    qualityChecks: string[];
  };
  hydroBuddyAnalysis?: {
    errorAnalysis: {
      overallAccuracy: 'excellent' | 'good' | 'fair' | 'poor';
      maxError: number;
      meanAbsoluteError: number;
      rootMeanSquareError: number;
    };
    metadata: {
      matrixCondition: number;
      solverMethod: string;
      calculationTime: number;
      warnings?: string[];
    };
    matrixCondition: number;
    solverMethod: string;
  };
}

// Main Workflow State
export interface WorkflowState {
  currentStage: WorkflowStage;
  stageStatus: Record<WorkflowStage, StageStatus>;
  
  // Stage Data
  waterProfile: WorkflowWaterProfile | null;
  formulation: WorkflowFormulation | null;
  saltSelection: WorkflowSaltSelection | null;
  stockSolution: WorkflowStockSolution | null;
  calculationResults: WorkflowCalculationResults | null;
  
  // Navigation & Validation
  canAdvanceToStage: Record<WorkflowStage, boolean>;
  navigationHistory: WorkflowStage[];
  
  // UI State
  loading: boolean;
  error: string | null;
  showValidationErrors: boolean;
  
  // Persistence
  workflowId?: string; // For saving/loading complete workflows
  lastSaved?: string;
  autoSave: boolean;
}

// Initial State
const initialState: WorkflowState = {
  currentStage: WorkflowStage.WATER_PROFILE,
  stageStatus: {
    [WorkflowStage.WATER_PROFILE]: { completed: false, validated: false },
    [WorkflowStage.CROP_FORMULATION]: { completed: false, validated: false },
    [WorkflowStage.SALT_MANAGEMENT]: { completed: false, validated: false },
    [WorkflowStage.STOCK_SOLUTIONS]: { completed: false, validated: false },
    [WorkflowStage.UNIFIED_CALCULATOR]: { completed: false, validated: false },
  },
  
  waterProfile: null,
  formulation: null,
  saltSelection: null,
  stockSolution: null,
  calculationResults: null,
  
  canAdvanceToStage: {
    [WorkflowStage.WATER_PROFILE]: true,
    [WorkflowStage.CROP_FORMULATION]: false,
    [WorkflowStage.SALT_MANAGEMENT]: false,
    [WorkflowStage.STOCK_SOLUTIONS]: false,
    [WorkflowStage.UNIFIED_CALCULATOR]: false,
  },
  
  navigationHistory: [WorkflowStage.WATER_PROFILE],
  loading: false,
  error: null,
  showValidationErrors: false,
  autoSave: true,
};

// Async Thunks for API calls
export const saveWorkflowData = createAsyncThunk(
  'workflow/saveWorkflowData',
  async (payload: { stage: WorkflowStage; data: any }) => {
    // Implementation will call appropriate API endpoints
    const response = await fetch(`/api/workflow/${payload.stage}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload.data),
    });
    return response.json();
  }
);

export const validateStageData = createAsyncThunk(
  'workflow/validateStageData',
  async (payload: { stage: WorkflowStage; data: any }) => {
    const response = await fetch(`/api/workflow/${payload.stage}/validate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload.data),
    });
    return response.json();
  }
);

// Workflow Slice
const workflowSlice = createSlice({
  name: 'workflow',
  initialState,
  reducers: {
    // Navigation Actions
    setCurrentStage: (state, action: PayloadAction<WorkflowStage>) => {
      // Sync route → currentStage when allowed (advance map, completed, or water always)
      const stage = action.payload;
      const allowed =
        stage === WorkflowStage.WATER_PROFILE ||
        state.canAdvanceToStage[stage] ||
        Boolean(state.stageStatus[stage]?.completed);
      if (allowed) {
        state.currentStage = stage;
        if (!state.navigationHistory.includes(stage)) {
          state.navigationHistory.push(stage);
        }
      }
    },
    
    goToNextStage: (state) => {
      const stages = Object.values(WorkflowStage);
      const currentIndex = stages.indexOf(state.currentStage);
      if (currentIndex < stages.length - 1) {
        const nextStage = stages[currentIndex + 1];
        if (state.canAdvanceToStage[nextStage]) {
          state.currentStage = nextStage;
          if (!state.navigationHistory.includes(nextStage)) {
            state.navigationHistory.push(nextStage);
          }
        }
      }
    },
    
    goToPreviousStage: (state) => {
      const stages = Object.values(WorkflowStage);
      const currentIndex = stages.indexOf(state.currentStage);
      if (currentIndex > 0) {
        state.currentStage = stages[currentIndex - 1];
      }
    },
    
    // Stage Data Actions
    setWaterProfile: (state, action: PayloadAction<WorkflowWaterProfile>) => {
      state.waterProfile = action.payload;
      state.stageStatus[WorkflowStage.WATER_PROFILE].completed = true;
      state.canAdvanceToStage[WorkflowStage.CROP_FORMULATION] = true;
    },
    
    setFormulation: (state, action: PayloadAction<WorkflowFormulation>) => {
      state.formulation = action.payload;
      state.stageStatus[WorkflowStage.CROP_FORMULATION].completed = true;
      state.canAdvanceToStage[WorkflowStage.SALT_MANAGEMENT] = true;
    },
    
    /**
     * Draft salt selection while user is still choosing salts.
     * Does NOT complete Stage 3 or unlock Stage 4 (P0 — confirm required).
     * Re-emitting the same salt IDs must NOT revoke Stage 4 (fixes confirm→navigate race).
     */
    setSaltSelection: (state, action: PayloadAction<WorkflowSaltSelection>) => {
      const nextIds = (action.payload.selectedSalts || [])
        .map((s) => s.id)
        .slice()
        .sort()
        .join(',');
      const prevIds = (state.saltSelection?.selectedSalts || [])
        .map((s) => s.id)
        .slice()
        .sort()
        .join(',');
      const sameSelection = nextIds === prevIds && nextIds.length > 0;

      state.saltSelection = action.payload;
      const hasSalts = (action.payload.selectedSalts?.length ?? 0) > 0;
      state.stageStatus[WorkflowStage.SALT_MANAGEMENT].validated = hasSalts;
      state.stageStatus[WorkflowStage.SALT_MANAGEMENT].errors = hasSalts
        ? undefined
        : ['At least one salt must be selected'];

      // Only revoke Stage 4+ when the set of salts actually changes
      if (!sameSelection) {
        state.stageStatus[WorkflowStage.SALT_MANAGEMENT].completed = false;
        state.canAdvanceToStage[WorkflowStage.STOCK_SOLUTIONS] = false;
        state.stageStatus[WorkflowStage.STOCK_SOLUTIONS] = {
          completed: false,
          validated: false,
        };
        state.canAdvanceToStage[WorkflowStage.UNIFIED_CALCULATOR] = false;
        state.stockSolution = null;
      }
    },

    /**
     * Explicit confirm: Stage 3 complete + unlock Stage 4.
     * Call only from "Proceed to Stock Solutions" (or equivalent).
     */
    confirmSaltSelection: (state, action: PayloadAction<WorkflowSaltSelection | undefined>) => {
      if (action.payload) {
        state.saltSelection = action.payload;
      }
      const hasSalts = (state.saltSelection?.selectedSalts?.length ?? 0) > 0;
      if (!hasSalts) {
        state.stageStatus[WorkflowStage.SALT_MANAGEMENT].completed = false;
        state.stageStatus[WorkflowStage.SALT_MANAGEMENT].validated = false;
        state.stageStatus[WorkflowStage.SALT_MANAGEMENT].errors = [
          'At least one salt must be selected',
        ];
        state.canAdvanceToStage[WorkflowStage.STOCK_SOLUTIONS] = false;
        return;
      }
      state.stageStatus[WorkflowStage.SALT_MANAGEMENT].completed = true;
      state.stageStatus[WorkflowStage.SALT_MANAGEMENT].validated = true;
      state.stageStatus[WorkflowStage.SALT_MANAGEMENT].errors = undefined;
      state.stageStatus[WorkflowStage.SALT_MANAGEMENT].timestamp = new Date().toISOString();
      state.canAdvanceToStage[WorkflowStage.STOCK_SOLUTIONS] = true;
    },
    
    setStockSolution: (state, action: PayloadAction<WorkflowStockSolution>) => {
      state.stockSolution = action.payload;
      state.stageStatus[WorkflowStage.STOCK_SOLUTIONS].completed = true;
      state.canAdvanceToStage[WorkflowStage.UNIFIED_CALCULATOR] = true;
    },
    
    setCalculationResults: (state, action: PayloadAction<WorkflowCalculationResults>) => {
      state.calculationResults = action.payload;
      state.stageStatus[WorkflowStage.UNIFIED_CALCULATOR].completed = true;
    },
    
    // Validation Actions
    setStageValidation: (state, action: PayloadAction<{
      stage: WorkflowStage;
      validated: boolean;
      errors?: string[];
      warnings?: string[];
    }>) => {
      const { stage, validated, errors, warnings } = action.payload;
      state.stageStatus[stage].validated = validated;
      state.stageStatus[stage].errors = errors;
      state.stageStatus[stage].warnings = warnings;
    },
    
    // UI Actions
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    
    setShowValidationErrors: (state, action: PayloadAction<boolean>) => {
      state.showValidationErrors = action.payload;
    },
    
    // Reset Actions
    resetWorkflow: (state) => {
      return { ...initialState };
    },
    
    resetFromStage: (state, action: PayloadAction<WorkflowStage>) => {
      const stages = Object.values(WorkflowStage);
      const resetIndex = stages.indexOf(action.payload);
      
      // Reset all stages from the specified stage onwards
      for (let i = resetIndex; i < stages.length; i++) {
        const stage = stages[i];
        state.stageStatus[stage] = { completed: false, validated: false };
        
        // Clear stage data
        if (stage === WorkflowStage.CROP_FORMULATION) state.formulation = null;
        if (stage === WorkflowStage.SALT_MANAGEMENT) state.saltSelection = null;
        if (stage === WorkflowStage.STOCK_SOLUTIONS) state.stockSolution = null;
        if (stage === WorkflowStage.UNIFIED_CALCULATOR) state.calculationResults = null;
        
        // Update navigation permissions
        if (i > resetIndex) {
          state.canAdvanceToStage[stage] = false;
        }
      }
      
      state.currentStage = action.payload;
    },
  },
  
  extraReducers: (builder) => {
    // Handle async thunk states
    builder
      .addCase(saveWorkflowData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveWorkflowData.fulfilled, (state, action) => {
        state.loading = false;
        state.lastSaved = new Date().toISOString();
        // Update stage status with saved data ID
        if (action.meta.arg.stage && action.payload.id) {
          state.stageStatus[action.meta.arg.stage].dataId = action.payload.id;
        }
      })
      .addCase(saveWorkflowData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to save workflow data';
      })
      .addCase(validateStageData.fulfilled, (state, action) => {
        const { stage } = action.meta.arg;
        const { valid, errors, warnings } = action.payload;
        state.stageStatus[stage].validated = valid;
        state.stageStatus[stage].errors = errors;
        state.stageStatus[stage].warnings = warnings;
      });
  },
});

export const {
  setCurrentStage,
  goToNextStage,
  goToPreviousStage,
  setWaterProfile,
  setFormulation,
  setSaltSelection,
  confirmSaltSelection,
  setStockSolution,
  setCalculationResults,
  setStageValidation,
  setLoading,
  setError,
  setShowValidationErrors,
  resetWorkflow,
  resetFromStage,
} = workflowSlice.actions;

export default workflowSlice.reducer;
